import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  internalAlphaApi,
  type AlphaDashboardSnapshot,
  type DailyChecklistSnapshot,
  type DefectEntry,
  type ExitCriteriaSnapshot,
  type RecommendationEntry,
  type SuccessMetricsSnapshot,
  type UXLogEntry,
  type WorkflowValidationSnapshot,
} from '../../api/internal-alpha-alpha001-runtime';

type Tab = 'overview' | 'checklist' | 'workflow' | 'recommendations' | 'defects' | 'ux' | 'exit' | 'metrics';

export function InternalAlphaPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<AlphaDashboardSnapshot | null>(null);
  const [checklist, setChecklist] = useState<DailyChecklistSnapshot | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowValidationSnapshot | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationEntry[]>([]);
  const [defects, setDefects] = useState<DefectEntry[]>([]);
  const [uxLog, setUXLog] = useState<UXLogEntry[]>([]);
  const [exit, setExit] = useState<ExitCriteriaSnapshot | null>(null);
  const [metrics, setMetrics] = useState<SuccessMetricsSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-amber-200 bg-white dark:border-amber-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-amber-700 dark:text-amber-300';
  const shell = 'bg-amber-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, chk, wf, rec, def, ux, ex, met] = await Promise.all([
        internalAlphaApi.dashboard(), internalAlphaApi.dailyChecklist(), internalAlphaApi.workflow(),
        internalAlphaApi.recommendationLog(), internalAlphaApi.defects(), internalAlphaApi.uxLog(),
        internalAlphaApi.exitCriteria(), internalAlphaApi.successMetrics(),
      ]);
      setDashboard(dash); setChecklist(chk); setWorkflow(wf); setRecommendations(rec);
      setDefects(def); setUXLog(ux); setExit(ex); setMetrics(met);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Internal Alpha data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'checklist', label: 'Daily Checklist' },
    { id: 'workflow', label: 'Workflow Validation' }, { id: 'recommendations', label: 'Recommendations' },
    { id: 'defects', label: 'Defects' }, { id: 'ux', label: 'UX Friction' },
    { id: 'exit', label: 'Exit Criteria' }, { id: 'metrics', label: 'Success Metrics' },
  ];

  const severityColor = (s: string) => {
    if (s === 'critical') return 'text-red-600 bg-red-50 dark:bg-red-900/30';
    if (s === 'high') return 'text-orange-600 bg-orange-50 dark:bg-orange-900/30';
    if (s === 'medium') return 'text-amber-600 bg-amber-50 dark:bg-amber-900/30';
    return 'text-slate-600 bg-slate-50 dark:bg-zinc-800';
  };

  const decisionColor = (d: string) => {
    if (d === 'accepted') return 'text-emerald-600';
    if (d === 'rejected') return 'text-red-600';
    return 'text-slate-500';
  };

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>ALPHA-000001 · Internal Alpha Operating & Validation</p>
            <h1 className="text-xl font-semibold">Founder Alpha Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Alpha Day <strong className={accent}>{dashboard.alpha_day}</strong></span>
                <span>Score <strong className={accent}>{dashboard.overall_validation_score}</strong></span>
                <span>Workflows <strong className={accent}>{dashboard.workflows_validated}/{dashboard.workflows_total}</strong></span>
                <span>Kartmanage-First <strong className={accent}>{dashboard.kartmanage_first_rate_pct}%</strong></span>
                <span>Time Saved <strong className={accent}>{dashboard.time_saved_hours_per_week}h/wk</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/screen-review" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Screen Review</Link>
            <Link to="/platform/internal-alpha-alpha001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading Internal Alpha data…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-amber-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Validation Score', value: dashboard.overall_validation_score },
            { label: 'Kartmanage-First', value: `${dashboard.kartmanage_first_rate_pct}%` },
            { label: 'Critical Defects', value: dashboard.defects_critical },
            { label: 'High Defects', value: dashboard.defects_high },
            { label: 'Rec. Adoption', value: `${dashboard.recommendation_adoption_pct}%` },
            { label: 'Time Saved / Week', value: `${dashboard.time_saved_hours_per_week}h` },
            { label: 'Exit Criteria', value: `${dashboard.exit_criteria_met}/${dashboard.exit_criteria_total}` },
            { label: 'Founder Daily Use', value: dashboard.founder_daily_use.replace('_', ' ') },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'checklist' && checklist && (
        <div className="grid gap-4 lg:grid-cols-3">
          {([
            ['Morning', checklist.morning, checklist.morning_completion_pct],
            ['During Day', checklist.during_day, checklist.during_completion_pct],
            ['End of Day', checklist.end_of_day, checklist.eod_completion_pct],
          ] as const).map(([title, items, pct]) => (
            <div key={title} className={`rounded-xl border p-4 ${card}`}>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold">{title}</h2>
                <span className={`text-sm ${accent}`}>{pct}%</span>
              </div>
              <ul className="space-y-2">
                {items.map((item) => (
                  <li key={item.key} className={`flex items-start gap-2 text-sm ${item.complete ? muted : 'font-medium'}`}>
                    <span>{item.complete ? '✓' : '○'}</span>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === 'workflow' && workflow && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className={`mb-3 text-sm ${muted}`}>{workflow.validated_count} validated · {workflow.gap_count} gaps · avg friction {workflow.avg_friction_score}</p>
          <div className="space-y-2">
            {workflow.steps.map((s) => (
              <div key={s.step} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-100 p-3 dark:border-amber-900/30">
                <div>
                  <p className="font-medium">{s.step}</p>
                  {s.gap && <p className="text-sm text-orange-600">{s.gap}</p>}
                </div>
                <div className="flex gap-3 text-sm">
                  <span className={s.kartmanage_first ? 'text-emerald-600' : 'text-orange-600'}>{s.kartmanage_first ? 'Kartmanage' : 'Seller Central'}</span>
                  <span className={muted}>{s.status}</span>
                  <span className={accent}>friction {s.friction_score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-2">
          {recommendations.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-medium">{r.recommendation}</p>
                <span className={`text-sm font-semibold capitalize ${decisionColor(r.decision)}`}>{r.decision}</span>
              </div>
              <p className={`mt-1 text-sm ${muted}`}>Evidence: {r.evidence} · Confidence {(r.confidence * 100).toFixed(0)}%</p>
              {r.actual_impact && <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-400">Impact: {r.actual_impact}</p>}
              {r.learning_outcome && <p className={`mt-1 text-sm ${accent}`}>Learning: {r.learning_outcome}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'defects' && (
        <div className="space-y-2">
          {defects.map((d) => (
            <div key={d.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${severityColor(d.severity)}`}>{d.severity}</span>
                <span className={`text-xs ${muted}`}>{d.status}</span>
              </div>
              <p className="mt-2 font-medium">{d.description}</p>
              <p className={`mt-1 text-sm ${muted}`}>Expected: {d.expected_behaviour}</p>
              <p className="text-sm text-orange-600">Actual: {d.actual_behaviour}</p>
              <p className={`mt-1 text-sm ${accent}`}>Impact: {d.business_impact}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'ux' && (
        <div className="space-y-2">
          {uxLog.map((u) => (
            <div key={u.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className={`rounded bg-amber-100 px-2 py-0.5 dark:bg-amber-900/40 ${accent}`}>{u.trigger.replace(/_/g, ' ')}</span>
                <span className={muted}>{u.screen}</span>
              </div>
              <p className="mt-2">{u.note}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'exit' && exit && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className={`mb-4 text-sm ${muted}`}>{exit.met_count}/{exit.total_count} met · {exit.ready_for_pilot ? 'Ready for Pilot' : 'Not ready for Pilot'}</p>
          <div className="space-y-2">
            {exit.criteria.map((c) => (
              <div key={c.key} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-amber-100 p-3 dark:border-amber-900/30">
                <div>
                  <p className="font-medium">{c.label}</p>
                  {c.detail && <p className={`text-sm ${muted}`}>{c.detail}</p>}
                </div>
                <span className={c.met ? 'text-emerald-600' : 'text-orange-600'}>{c.met ? 'Met' : 'Pending'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'metrics' && metrics && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Time Saved / Week', value: `${metrics.time_saved_hours_week}h` },
            { label: 'Profit Improvement', value: `${metrics.profit_improvement_pct}%` },
            { label: 'Revenue Improvement', value: `${metrics.revenue_improvement_pct}%` },
            { label: 'Recommendation Accuracy', value: `${metrics.recommendation_accuracy_pct}%` },
            { label: 'Automation Rate', value: `${metrics.automation_rate_pct}%` },
            { label: 'Workflow Completion', value: `${metrics.workflow_completion_pct}%` },
            { label: 'Customer Value Growth', value: `${metrics.customer_value_growth_pct}%` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
